import { useEffect, useRef, useState } from 'react'
import { useTheme } from '@emotion/react'
import { css } from '@emotion/css'
import styled from '@emotion/styled'
import { inject, observer } from 'mobx-react'

import { API_URL } from '../../api'
import { withRouterProps } from '../../utils'
import {
  Button,
  Container as Content,
  ErrorPage,
  PageLoading,
  PageLoadingContainer,
} from '../../components'
import Page, { Header } from '../onboarding/elements/Page'
import BGRight from '../../assets/images/BGRight.svg'
import AleoBold from '../../assets/fonts/aleo/Aleo-Bold.otf'
import OpenSansLight from '../../assets/fonts/Open_Sans/OpenSans-Light.ttf'
import OpenSansLightItalic from '../../assets/fonts/Open_Sans/OpenSans-LightItalic.ttf'
import OpenSansRegular from '../../assets/fonts/Open_Sans/OpenSans-Regular.ttf'
import OpenSansBold from '../../assets/fonts/Open_Sans/OpenSans-Bold.ttf'

// Render the backend HTML in an iframe so its styles do not affect the app.
const DisclaimerFrame = styled.iframe`
  width: 100%;
  border: none;
  overflow: hidden;
`

const Footer = styled.div`
  display: flex;
  margin: 36px 0 60px;

  button {
    margin: 0;
  }
`

const rightBackground = theme => css`
  background-image: url(${API_URL && theme.bgRight ? API_URL + theme.bgRight : BGRight});
  background-repeat: no-repeat;
  background-position: top right;
  background-attachment: fixed;
`

const FONT_WAIT_CAP_MS = 500

const fontUrl = file => new URL(file, window.location.origin).href

const disclaimerStyles = theme => `
  /* Show fallback text while the custom fonts load. */
  @font-face { font-family: 'Aleo'; src: url('${fontUrl(AleoBold)}') format('opentype'); font-weight: 700; font-display: swap; }
  @font-face { font-family: 'Open Sans'; src: url('${fontUrl(OpenSansLight)}') format('truetype'); font-weight: 300; font-display: swap; }
  @font-face { font-family: 'Open Sans'; src: url('${fontUrl(OpenSansLightItalic)}') format('truetype'); font-weight: 300; font-style: italic; font-display: swap; }
  @font-face { font-family: 'Open Sans'; src: url('${fontUrl(OpenSansRegular)}') format('truetype'); font-weight: 400; font-display: swap; }
  @font-face { font-family: 'Open Sans'; src: url('${fontUrl(OpenSansBold)}') format('truetype'); font-weight: 700; font-display: swap; }

  /* Prevent nested scrolling; the iframe expands to the document height. */
  html, body { overflow: hidden; }

  body {
    font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
    font-weight: 300;
    font-size: 18px;
    line-height: 24px;
    color: ${theme.mediumGray};
  }
  h1 {
    font-family: 'Aleo', serif;
    font-weight: 700;
    font-size: 50px;
    color: ${theme.darkestGray};
  }
  h2 {
    font-family: 'Aleo', serif;
    font-weight: 700;
    font-size: 24px;
    color: ${theme.darkestGray};
  }
  h3 {
    font-family: 'Aleo', serif;
    font-weight: 700;
    font-size: 20px;
    color: ${theme.darkestGray};
  }
  .welcome, p, ul, ol, li {
    font-size: 18px;
    line-height: 24px;
  }
  p.bold, strong, b {
    font-weight: 700;
  }
  a {
    color: ${theme.primaryColor};
    text-decoration: underline;
  }
  /* Override the document's more specific 3Nickels contact colors. */
  .contact-info {
    color: ${theme.mediumGray};
  }
  .contact-info a {
    color: ${theme.primaryColor};
  }
`

function AiDisclaimerPage({ store, navigate, location }) {
  const theme = useTheme()
  const [status, setStatus] = useState('loading')
  const [frameHeight, setFrameHeight] = useState(null)

  const frameRef = useRef(null)
  const frameResizeObserverRef = useRef(null)

  const ready = frameHeight !== null
  const returnTo = location.state?.from ?? null
  const returnState = location.state?.fromState ?? null

  useEffect(() => {
    let active = true

    ;(async () => {
      try {
        if (!store.aiDisclaimerHtml) await store.getAiDisclaimer()
        if (active) setStatus('done')
      } catch (err) {
        console.error('Failed to load the AI disclaimer:', err)
        if (active) setStatus('error')
      }
    })()

    return () => {
      active = false
    }
  }, [store])

  // Outside the app shell, so this page carries the standalone background itself.
  useEffect(() => {
    document.documentElement.className = rightBackground(theme)
    return () => {
      document.documentElement.className = ''
    }
  }, [theme])

  useEffect(() => () => frameResizeObserverRef.current?.disconnect(), [])

  // App styles cannot cross the iframe boundary, so they are injected instead.
  const injectDisclaimerStyles = frameDoc => {
    if (frameDoc.getElementById('gc-disclaimer-styles')) return

    const style = frameDoc.createElement('style')
    style.id = 'gc-disclaimer-styles'
    style.textContent = disclaimerStyles(theme)
    frameDoc.head.appendChild(style)
  }

  // Use body.scrollHeight so the frame can shrink; add the excluded body margins.
  const measureFrameHeight = frameDoc => {
    const bodyStyle = frameDoc.defaultView?.getComputedStyle(frameDoc.body)
    const margins =
      (parseFloat(bodyStyle?.marginTop) || 0) + (parseFloat(bodyStyle?.marginBottom) || 0)

    setFrameHeight(`${frameDoc.body.scrollHeight + margins}px`)
  }

  const onFrameLoad = async () => {
    const frame = frameRef.current
    if (!frame) return

    try {
      const frameDoc = frame.contentDocument || frame.contentWindow?.document
      if (!frameDoc) throw new Error('The AI disclaimer document is unavailable')

      injectDisclaimerStyles(frameDoc)

      // Load injected fonts before measuring; `fonts.ready` can stall on a broken font face.
      const fontsLoaded = Promise.allSettled([
        frameDoc.fonts?.load('700 50px Aleo'),
        frameDoc.fonts?.load('300 18px "Open Sans"'),
        frameDoc.fonts?.load('italic 300 18px "Open Sans"'),
        frameDoc.fonts?.load('400 18px "Open Sans"'),
        frameDoc.fonts?.load('700 18px "Open Sans"'),
      ])

      // A font that hangs rather than rejects must not strand the page on the spinner.
      await Promise.race([
        fontsLoaded,
        new Promise(resolve => setTimeout(resolve, FONT_WAIT_CAP_MS)),
      ])

      // Ignore the result if the frame changed during the wait.
      if (frameRef.current !== frame) return

      measureFrameHeight(frameDoc)

      frameResizeObserverRef.current?.disconnect()
      const observer = new ResizeObserver(() => measureFrameHeight(frameDoc))
      observer.observe(frameDoc.body)
      frameResizeObserverRef.current = observer
    } catch (err) {
      console.error('Failed to size the AI disclaimer iframe:', err)
      if (frameRef.current === frame) setStatus('error')
    }
  }

  const goBack = () => (returnTo ? navigate(returnTo, { state: returnState }) : navigate(-1))

  if (status === 'error') return <ErrorPage />

  return (
    <Page.Container>
      <Header />

      {!ready && (
        <PageLoadingContainer>
          <PageLoading />
        </PageLoadingContainer>
      )}

      {status === 'done' && (
        <Content>
          {/* `allow-same-origin` permits contentDocument access without scripts;
          `allow-top-navigation-to-custom-protocols` permits mailto: and tel: links. */}
          <DisclaimerFrame
            ref={frameRef}
            title='AI Disclaimer'
            sandbox='allow-same-origin allow-top-navigation-to-custom-protocols'
            srcDoc={store.aiDisclaimerHtml ?? ''}
            onLoad={onFrameLoad}
            aria-hidden={!ready}
            style={{ height: frameHeight ?? 0, visibility: ready ? 'visible' : 'hidden' }}
          />

          {ready && (
            <Footer>
              <Button type='button' variant='secondary' onClick={goBack}>
                Back
              </Button>
            </Footer>
          )}
        </Content>
      )}
    </Page.Container>
  )
}

export default withRouterProps(inject('store')(observer(AiDisclaimerPage)))
